/**
 * PII Capture Page — DRR-302
 *
 * Mobile-first page for capturing PII from documents (SSN cards, driver's licenses,
 * insurance cards) via camera. Uses Presidio OCR + PII detection on the backend.
 *
 * Routes to: /ferp-core/pii-capture
 */

import { Container, Title, Text, Stack, Paper, Alert, Badge, Group } from '@mantine/core';
import { IconCamera, IconInfoCircle, IconCheck } from '@tabler/icons-react';
import { PiiCardScanner } from '@ferp/workflow-forms';
import { useState } from 'react';

interface CapturedResult {
  tokens: Record<string, string>;
  documentType: string | null;
}

export function PiiCapturePage() {
  const [result, setResult] = useState<CapturedResult | null>(null);

  const handleComplete = (data: CapturedResult) => {
    setResult(data);
  };

  return (
    <Container size="sm" py="xl">
      <Stack gap="lg">
        <div>
          <Title order={2}>
            <IconCamera size={28} style={{ verticalAlign: 'middle', marginRight: 8 }} />
            PII Document Capture
          </Title>
          <Text c="dimmed" size="sm" mt="xs">
            Capture SSN cards, driver's licenses, or insurance cards.
            All PII is encrypted and stored securely in the vault.
          </Text>
        </div>

        <Alert icon={<IconInfoCircle size={16} />} color="blue">
          Point your camera at a document. PII will be automatically detected
          and masked. You can review before storing.
        </Alert>

        {!result ? (
          <Paper shadow="sm" p="md" withBorder>
            <PiiCardScanner
              subjectId="test-subject"
              onComplete={handleComplete}
            />
          </Paper>
        ) : (
          <Paper shadow="sm" p="md" withBorder>
            <Group mb="md">
              <IconCheck size={24} color="green" />
              <Title order={4}>PII Captured Successfully</Title>
            </Group>

            {result.documentType && (
              <Badge color="blue" size="lg" mb="md">
                {result.documentType}
              </Badge>
            )}

            <Text size="sm" fw={500} mb="xs">Vault Tokens:</Text>
            <Stack gap="xs">
              {Object.entries(result.tokens).map(([entityType, token]) => (
                <Paper key={entityType} p="xs" withBorder>
                  <Text size="sm" fw={500}>{entityType}</Text>
                  <Text size="xs" c="blue" style={{ fontFamily: 'monospace' }}>
                    {token}
                  </Text>
                </Paper>
              ))}
            </Stack>

            <Text size="xs" c="dimmed" mt="md">
              These tokens can be used to retrieve the encrypted PII values when needed.
            </Text>
          </Paper>
        )}
      </Stack>
    </Container>
  );
}

export default PiiCapturePage;
